/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import PageContainer from "../../components/PageContainer";
import { useUI } from "../../theme/ThemeProvider";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import { FiPlus, FiSave, FiSearch, FiCheck, FiX, FiShoppingCart, FiTrash2, FiMinus } from "react-icons/fi";
import Swal from "sweetalert2";
import { pickActiveDeliveryCompany, initiatePayment } from "../../../Stores/utils/storeApi";

export default function CreateOrderPage() {
    const { lang, theme, colors } = useUI();
    const isArabic = lang === "ar";
    const isDark = theme === "dark";
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isManualParam = searchParams.get("isManual") === "1" ? 1 : 0;
    const editOrderId = searchParams.get("id") || "";
    const isEditMode = !!editOrderId;

    const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api";
    const tenantId = parseInt(localStorage.getItem("tenantId") || "-1");
    const baseImageUrl = baseUrl.replace("/api", "");
    const asText = (value) => (value === null || value === undefined ? "" : String(value).trim());
    const asNumber = (value, fallback = 0) => {
        const n = Number(value);
        return Number.isFinite(n) ? n : fallback;
    };

    // --- State ---
    const [loading, setLoading] = useState(false);
    const [products, setProducts] = useState([]);
    const [phase, setPhase] = useState(1); // 1: Select Products, 2: Customer Details
    const [editingOrder, setEditingOrder] = useState(null);

    // Cart State
    const [cartItems, setCartItems] = useState([]);

    // Selection State
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [selectedSize, setSelectedSize] = useState(null); // الحجم المختار
    const [qty, setQty] = useState(1);

    // Form Data (Phase 2)
    const [formData, setFormData] = useState({
        customerName: "",
        customerPhone1: "",
        city: "",
        address: "",
        status: "new",
        deliveryFee: 0,
        deliveryId: 0
    });

    const [deliveryInfo, setDeliveryInfo] = useState(null);
    const mapOrderDetailToCartItem = (item, index = 0) => {
        const quantity = asNumber(item.quantity, 0);
        const price = asNumber(item.price, 0);
        const stockQty = Math.max(asNumber(item.stockQty, 0), quantity, 1);

        return {
            tempid: `${item.variantId || item.itemId || "item"}-${index}`,
            detailId: asNumber(item.id, 0),
            itemId: asNumber(item.itemId, 0),
            itemName: asText(item.itemName),
            variantId: asNumber(item.variantId, 0),
            variantName: asText(item.variantName),
            quantity,
            price,
            total: parseFloat((price * quantity).toFixed(2)),
            originalPrice: price,
            costPrice: asNumber(item.costPrice, 0), // ✅ حفظ سعر التكلفة
            color: asText(item.colorName),
            size: asText(item.size),
            imageUrl: asText(item.imageUrl),
            stock: stockQty,
            originalVariant: null
        };
    };

    // --- Fetch Cities ---
    const [cities, setCities] = useState([]);
    useEffect(() => {
        const fetchCities = async () => {
            try {
                const res = await fetch(`${baseUrl}/City/GetCities`);
                if (res.ok) {
                    const data = await res.json();
                    setCities(Array.isArray(data) ? data : []);
                }
            } catch (err) {
                console.error("Error fetching cities", err);
            }
        };
        fetchCities();
    }, [baseUrl]);

    useEffect(() => {
        if (!isEditMode) return;

        const hydrateOrderForEditing = async () => {
            try {
                let targetOrder = location.state?.order || null;

                if (!targetOrder) {
                    const token = localStorage.getItem("authToken");
                    const res = await fetch(`${baseUrl}/GetTenantOrders?tenantId=${tenantId}&statusId=-1`, {
                        headers: {
                            Accept: "application/json",
                            Authorization: token ? `Bearer ${token}` : "",
                        },
                    });

                    if (!res.ok) throw new Error("Failed to fetch order for editing");
                    const data = await res.json();
                    if (Array.isArray(data)) {
                        targetOrder = data.find(o => String(o.orderId) === String(editOrderId)) || null;
                    }
                }

                if (!targetOrder) return;

                setEditingOrder(targetOrder);
                setCartItems(Array.isArray(targetOrder.details) ? targetOrder.details.map(mapOrderDetailToCartItem) : []);
                setFormData(prev => ({
                    ...prev,
                    customerName: targetOrder.customerName || "",
                    customerPhone1: targetOrder.customerPhone1 || "",
                    city: targetOrder.city || "",
                    address: targetOrder.address || "",
                    status: targetOrder.status || "new",
                    deliveryFee: 0,
                    deliveryId: Number(targetOrder.deliveryId || 0)
                }));
                setPhase(1);
            } catch (err) {
                console.error("Error hydrating order for edit", err);
            }
        };

        hydrateOrderForEditing();
    }, [isEditMode, editOrderId, location.state, baseUrl, tenantId]);

    // --- Fetch Delivery Info when city changes ---
    useEffect(() => {
        const selectedCity = cities.find(c => c.cityName === formData.city);
        if (selectedCity && tenantId !== -1) {
            fetch(`${baseUrl}/TenantDelivery/GetByCity?tenantId=${tenantId}&cityId=${selectedCity.cityId}`)
                .then(res => res.json())
                .then(data => {
                    const delivery = pickActiveDeliveryCompany(data);
                    if (delivery) {
                        setDeliveryInfo(delivery);
                        setFormData(prev => ({
                            ...prev,
                            deliveryFee: Number(delivery.deliveryCompanyFees || 0),
                            deliveryId: Number(delivery.deliveryId || 0)
                        }));
                    } else {
                        setDeliveryInfo(null);
                        setFormData(prev => ({ ...prev, deliveryFee: 0, deliveryId: 0 }));
                    }
                })
                .catch(err => {
                    console.error("Error fetching delivery info", err);
                    setDeliveryInfo(null);
                    setFormData(prev => ({ ...prev, deliveryFee: 0, deliveryId: 0 }));
                });
        } else {
            setDeliveryInfo(null);
            setFormData(prev => ({ ...prev, deliveryFee: 0, deliveryId: 0 }));
        }
    }, [formData.city, cities, tenantId, baseUrl]);

    // --- Fetch Products & Stock ---
    useEffect(() => {
        const fetchProductsAndStock = async () => {
            try {
                const token = localStorage.getItem("authToken");
                const headers = token ? { Authorization: `Bearer ${token}` } : {};

                const [resItems, resStock] = await Promise.all([
                    fetch(`${baseUrl}/GetItems?tenantId=${tenantId}&itemId=-1`, { headers }),
                    fetch(`${baseUrl}/ItemsStock?tenantId=${tenantId}&itemId=-1`, { headers })
                ]);

                if (resItems.ok) {
                    const itemsData = await resItems.json();
                    let stockData = [];
                    if (resStock.ok) {
                        stockData = await resStock.json();
                    }

                    // Merge stock into items
                    const productsWithStock = itemsData.map(item => {
                        const itemId = item.itemId || item.ItemId;
                        const itemStock = Array.isArray(stockData) ? stockData.filter(s => (s.itemId || s.ItemId) === itemId) : [];
                        return {
                            ...item,
                            itemId: itemId, // Ensure consistent casing
                            variants: item.variants?.map(v => {
                                const variantId = v.variantId || v.VariantId;
                                const vStockEntry = itemStock.find(s => (s.variantId || s.VariantId || s.id || s.Id) === variantId);
                                const available = vStockEntry ? (vStockEntry.availableStock ?? vStockEntry.AvailableStock ?? vStockEntry.quantity ?? vStockEntry.Quantity ?? vStockEntry.totalStock ?? 0) : 0;
                                 return {
                                    ...v,
                                    variantId: variantId, // Ensure consistent casing
                                    stockQty: Number(available),
                                    sizes: v.sizes || [],
                                    costPrice: Number(v.costPrice || 0) // ✅ حفظ سعر التكلفة
                                };
                            })
                        };
                    });

                    setProducts(productsWithStock);
                }
            } catch (err) {
                console.error("Error fetching items or stock", err);
            }
        };
        if (tenantId !== -1) fetchProductsAndStock();
    }, [baseUrl, tenantId]);


    // --- Helper: Filter Products ---
    const filteredProducts = useMemo(() => {
        if (!searchTerm) return products;
        return products.filter(p => p.itemName.toLowerCase().includes(searchTerm.toLowerCase()));
    }, [products, searchTerm]);

    // --- Helper: Build Image URL ---
    const getProductImage = (item) => {
        const path = item.mainImageUrl;
        if (!path) return "https://placehold.co/150x150/e0e0e0/666?text=No+Image";
        if (path.startsWith("http")) return path;
        return `${baseImageUrl}${path}`;
    };

    const getSizeStock = (size) => Number(size?.stockQty ?? 0);
    const getVariantTotalStock = (variant) => {
        if (Array.isArray(variant?.sizes) && variant.sizes.length > 0) {
            return variant.sizes.reduce((sum, sz) => sum + getSizeStock(sz), 0);
        }
        return Number(variant?.stockQty ?? 0);
    };
    const getProductTotalStock = (product) =>
        (product?.variants || []).reduce((sum, v) => sum + getVariantTotalStock(v), 0);


    // --- Step 1 Actions: Add Item to Local Cart ---
    const handleAddToLocalCart = () => {
        if (!selectedVariant || !selectedSize) return;
        const finalPrice = calculatePriceAfterDiscount(
            selectedVariant.price,
            selectedProduct.discountValue, // الخصم مخزن في المنتج الرئيسي
            selectedProduct.discountType
        );
        const enteredQty = parseInt(qty);
        const availableStock = selectedSize.stockQty || 0;

        if (availableStock <= 0) {
            Swal.fire({
                icon: "warning",
                title: isArabic ? "انتهى من المخزون" : "Out of stock",
                text: isArabic ? "هذا المنتج نفذ من المخزون ولا يمكن إضافته" : "This item is out of stock and cannot be added",
                confirmButtonText: isArabic ? "حسنًا" : "OK",
                confirmButtonColor: colors.mainColor,
                background: isDark ? "#1e293b" : "#ffffff",
                color: isDark ? "#f1f5f9" : "#111827",
            });
            return;
        }

        // التحقق من الكمية
        if (enteredQty > availableStock) {
            Swal.fire({
                icon: "warning",
                title: isArabic ? "تنبيه" : "Warning",
                text: isArabic
                    ? `الكمية المدخلة (${enteredQty}) أكبر من المخزون المتاح (${availableStock})`
                    : `Entered quantity (${enteredQty}) exceeds available stock (${availableStock})`,
                confirmButtonText: isArabic ? "حسناً" : "OK",
                confirmButtonColor: colors.mainColor,
                background: isDark ? "#1e293b" : "#ffffff",
                color: isDark ? "#f1f5f9" : "#111827",
                didOpen: () => {
                    const confirmBtn = Swal.getConfirmButton();
                    if (confirmBtn) {
                        confirmBtn.style.opacity = "1";
                        confirmBtn.style.visibility = "visible";
                        confirmBtn.style.display = "inline-block";
                        confirmBtn.style.color = "#ffffff";
                    }
                }
            });
            return;
        }

        if (enteredQty <= 0) {
            Swal.fire({
                icon: "error",
                title: isArabic ? "خطأ" : "Error",
                text: isArabic ? "الرجاء إدخال كمية صحيحة" : "Please enter a valid quantity",
                confirmButtonText: isArabic ? "حسناً" : "OK",
                confirmButtonColor: colors.mainColor,
                background: isDark ? "#1e293b" : "#ffffff",
                color: isDark ? "#f1f5f9" : "#111827",
                didOpen: () => {
                    const confirmBtn = Swal.getConfirmButton();
                    if (confirmBtn) {
                        confirmBtn.style.opacity = "1";
                        confirmBtn.style.visibility = "visible";
                        confirmBtn.style.display = "inline-block";
                        confirmBtn.style.color = "#ffffff";
                    }
                }
            });
            return;
        }

        const newItem = {
            tempid: Date.now(), // Temporary ID for frontend key
            itemId: selectedProduct.itemId,
            itemName: selectedProduct.itemName,
            variantId: selectedVariant.variantId,
            variantName: selectedVariant.variantName,
            quantity: enteredQty,
            price: finalPrice, // نبعث السعر النهائي (بعد الخصم)
            total: parseFloat((finalPrice * enteredQty).toFixed(2)),
            originalPrice: selectedVariant.price, // اختياري للعرض فقط
            costPrice: asNumber(selectedVariant.costPrice, 0), // ✅ سعر التكلفة من الـ API

            color: selectedVariant.color?.name,
            size: selectedSize.size, // الحجم المختار
            imageUrl: selectedVariant.imageUrl || selectedProduct?.mainImageUrl,
            stock: availableStock, // مخزون الحجم المحدد
            originalVariant: selectedVariant // Keep reference
        };

        setCartItems([...cartItems, newItem]);

        // Reset Selection
        setSelectedProduct(null);
        setSelectedVariant(null);
        setSelectedSize(null);
        setQty(1);
    };

    const handleRemoveItem = (tempId) => {
        setCartItems(cartItems.filter(i => i.tempid !== tempId));
    };
    const handleUpdateLocalQty = (tempId, nextQty) => {
        setCartItems(prev =>
            prev.map(item => {
                if (item.tempid !== tempId) return item;

                const maxAllowed = Math.max(asNumber(item.stock, 0), asNumber(item.quantity, 0), 1);
                const safeQty = Math.min(Math.max(asNumber(nextQty, 1), 1), maxAllowed);

                return {
                    ...item,
                    quantity: safeQty,
                    total: parseFloat((asNumber(item.price, 0) * safeQty).toFixed(2))
                };
            })
        );
    };
    const calculatePriceAfterDiscount = (originalPrice, discountValue, discountType) => {
        const price = parseFloat(originalPrice) || 0;
        const discount = parseFloat(discountValue) || 0;
        if (discount <= 0) return price;

        let finalPrice = price;
        if (discountType === 1) { // نسبة مئوية
            finalPrice = price - (price * (discount / 100));
        } else { // مبلغ ثابت
            finalPrice = price - discount;
        }

        // التقريب لرقمين بعد الفاصلة
        return parseFloat(finalPrice.toFixed(2));
    };
    const getDiscountedPrice = (price, discountValue, type) => {
        const p = Number(price) || 0;
        const d = Number(discountValue) || 0;
        if (d <= 0) return p;
        // منطق الـ storeApi: 1 = نسبة مئوية، 0 = مبلغ ثابت
        return type === 1 ? p - (p * d / 100) : p - d;
    };
    // --- Step 2 Actions: Finalize Order ---
    const handleFinalizeOrder = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const token = localStorage.getItem("authToken");
            if (!token) throw new Error("No auth token");

            let finalOrderId = "";
            let finalOrderNo = 0;

            if (isEditMode && editingOrder) {
                finalOrderId = editingOrder.orderId || editingOrder.id || editOrderId;
                finalOrderNo = Number(editingOrder.orderNo || editingOrder.orderNumber || 0);
            } else {
                const firstItem = cartItems[0];
                if (!firstItem) throw new Error("Accidental empty cart");

                const initPayload = {
                    tenantId: asNumber(tenantId),
                    customerId: 0,
                    variantId: asNumber(firstItem.variantId),
                    quantity: asNumber(firstItem.quantity),
                    price: asNumber(firstItem.price),
                    discount: 0,
                    size: asText(firstItem.size)
                };

                const resInit = await fetch(`${baseUrl}/Cart/AddToCart`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`,
                    },
                    body: JSON.stringify(initPayload)
                });

                if (!resInit.ok) throw new Error("Failed to initialize order");
                const initData = await resInit.json();

                if (typeof initData === 'object' && initData !== null) {
                    finalOrderId = initData.orderId || initData.id || "";
                    finalOrderNo = Number(initData.orderNo || initData.orderNumber || 0);
                } else if (typeof initData === 'string') {
                    finalOrderId = initData;
                }
            }

            if (!finalOrderId) throw new Error("No Order ID returned");

            const selectedCity = cities.find(c => c.cityName === formData.city);
            const finalCityId = selectedCity ? selectedCity.cityId : 0;

            const subtotal = cartItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
            const shippingFee = Number(formData.deliveryFee || 0);
            const totalWithShipping = subtotal + shippingFee;

            // 2. تجهيز البيانات النهائية (التصحيح هنا)
            const updatePayload = {
                orderId: String(finalOrderId),
                orderNo: asNumber(finalOrderNo),
                tenantID: asNumber(tenantId),
                statusId: asNumber(editingOrder?.statusId, 1),
                status: asText(editingOrder?.status || formData.status || "new"),

                totalAmount: asNumber(subtotal),
                discountAmount: asNumber(editingOrder?.discountAmount, 0),
                netAmount: asNumber(totalWithShipping),
                deliveryId: asNumber(formData.deliveryId),
                customerId: asNumber(editingOrder?.customerId, 0),

                customerName: asText(formData.customerName),
                customerPhone1: asText(formData.customerPhone1),
                cityId: asNumber(finalCityId),
                city: asText(formData.city),
                address: asText(formData.address),

                paymentMethodName: asText(editingOrder?.paymentMethodName || "Cash"),
                paymentStatus: asText(editingOrder?.paymentStatus || "Unpaid"),
                isPaid: editingOrder?.isPaid === true || editingOrder?.isPaid === 1 ? 1 : 0,
                isManual: editingOrder ? (editingOrder.isManual === 1 || editingOrder.isManual === true ? 1 : 0) : (Number(isManualParam) === 1 ? 1 : 0),
                orderDate: editingOrder?.orderDate || new Date().toISOString(),
                transactionId: asText(editingOrder?.transactionId || (isManualParam === 1 ? "manual" : "")),
                coupon: asText(editingOrder?.coupon || ""),

                details: cartItems.map(item => ({
                    id: asNumber(item.detailId, 0),
                    itemId: asNumber(item.itemId),
                    variantId: asNumber(item.variantId),
                    quantity: asNumber(item.quantity),
                    price: asNumber(item.price),
                    lineTotal: asNumber(item.price) * asNumber(item.quantity),
                    itemName: asText(item.itemName),
                    variantName: asText(item.variantName),
                    colorName: asText(item.color),
                    size: asText(item.size),
                    imageUrl: asText(item.imageUrl),
                    stockQty: Math.max(asNumber(item.stock), asNumber(item.quantity)),
                    discount: 0,
                     costPrice: asNumber(item.costPrice ?? item.originalVariant?.costPrice, 0)
                }))
            };

            const resUpdate = await fetch(`${baseUrl}/UpdateTenantOrder?tenantid=${tenantId}`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify(updatePayload)
            });

            if (!resUpdate.ok) {
                const errorData = await resUpdate.json();
                console.error("Server Validation Error:", errorData);
                throw new Error("Failed to save order details");
            }

            const customerFullName = asText(formData.customerName);
            const [firstName = "", ...lastNameParts] = customerFullName.split(/\s+/);
            const lastName = lastNameParts.join(" ");
            const paymentRes = await initiatePayment({
                tenantId: asNumber(tenantId),
                orderId: finalOrderId,
                email: asText(editingOrder?.email || ""),
                phone: asText(formData.customerPhone1),
                firstName,
                lastName
            });

            const redirectUrl =
                paymentRes?.success && paymentRes?.paymentUrl
                    ? String(paymentRes.paymentUrl).trim()
                    : "";

            if (redirectUrl) {
                window.location.replace(redirectUrl);
                return;
            }

            console.warn("CreateOrderPage: no payment URL after save", finalOrderId, paymentRes);
            navigate("/admin-store/orders");

        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: isArabic ? "حدث خطأ أثناء حفظ الطلب" : "Error saving order",
                timer: 2000,
                showConfirmButton: false,
                background: isDark ? "#1e293b" : "#ffffff", // خلفية غامقة أو فاتحة حسب الثيم
                color: isDark ? "#f1f5f9" : "#111827",      // لون النص
                confirmButtonColor: colors.mainColor, didOpen: () => {
                    const confirmBtn = Swal.getConfirmButton();
                    if (confirmBtn) {
                        confirmBtn.style.opacity = "1";
                        confirmBtn.style.visibility = "visible";
                        confirmBtn.style.display = "inline-block";
                        confirmBtn.style.color = "#ffffff"; // إجبار لون النص على الأبيض
                    }
                }
            });
        } finally {
            setLoading(false);
        }
    };


    // --- Render ---

    // PHASE 1: SELECT PRODUCTS
    if (phase === 1) {
        return (
            <PageContainer title={isArabic ? (isEditMode ? "تعديل الطلب - اختر المنتجات" : "إنشاء طلب - اختر المنتجات") : (isEditMode ? "Edit Order - Select Products" : "Create Order - Select Products")}>

                {/* Cart Floating Summary */}
                {cartItems.length > 0 && (
                    <div className={clsx(
                        "fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[90%] max-w-2xl p-4 rounded-2xl shadow-2xl flex items-center justify-between border backdrop-blur-md",
                        isDark ? "bg-[#1e293b]/90 border-gray-600 text-white" : "bg-white/90 border-gray-200 text-gray-900"
                    )}>
                        <div className="flex items-center gap-3">
                            <div className="bg-[#5B2C6F] w-10 h-10 rounded-full flex items-center justify-center text-white font-bold">
                                {cartItems.length}
                            </div>
                            <div className="flex flex-col">
                                <span className="font-bold text-sm">{isArabic ? "العناصر المضافة" : "Items Added"}</span>
                                <span className="text-xs opacity-70">
                                    {cartItems.reduce((acc, i) => acc + i.total, 0)} د.ع
                                </span>
                            </div>
                        </div>
                        <button
                            onClick={() => setPhase(2)}
                            className="px-6 py-2 rounded-xl bg-[#5B2C6F] text-white font-bold text-sm shadow-lg transition-all"
                        >
                            {isArabic ? "التالي: التفاصيل" : "Next: Details"}
                        </button>
                    </div>
                )}

                {/* Modal for Variant Selection */}
                {selectedProduct && (
                    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                        <div className={clsx(
                            "w-full max-w-2xl rounded-2xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto",
                            isDark ? "bg-[#1e293b] border border-gray-700" : "bg-white"
                        )}>
                            <button
                                onClick={() => {
                                    setSelectedProduct(null);
                                    setSelectedVariant(null);
                                    setSelectedSize(null);
                                }}
                                className={clsx(
                                    "absolute top-4 ltr:right-4 rtl:left-4 p-2 rounded-full transition-all z-10",
                                    isDark ? "bg-gray-800 hover:bg-gray-700 text-gray-400" : "bg-gray-100 hover:bg-gray-200 text-gray-600"
                                )}
                            >
                                <FiX size={20} />
                            </button>

                            {/* قسم معلومات المنتج الأساسية */}
                            <div className={clsx(
                                "mb-6 pb-6 border-b-2",
                                isDark ? "border-gray-700" : "border-gray-200"
                            )}>
                                <h2 className="text-sm uppercase tracking-wider text-gray-500 mb-4 font-semibold">
                                    {isArabic ? "معلومات المنتج" : "Product Information"}
                                </h2>

                                <div className="flex gap-4">
                                    {/* صورة المنتج الرئيسية */}
                                    <div className="w-28 h-28 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700">
                                        <img
                                            src={selectedProduct.mainImageUrl
                                                ? (selectedProduct.mainImageUrl.startsWith('http')
                                                    ? selectedProduct.mainImageUrl
                                                    : `${baseImageUrl}${selectedProduct.mainImageUrl}`)
                                                : "https://placehold.co/150x150/e0e0e0/666?text=No+Image"
                                            }
                                            alt={selectedProduct.itemName}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>

                                    {/* معلومات المنتج */}
                                    <div className="flex-1">
                                        <div className="flex items-start justify-between gap-4 mb-2">
                                            <div>
                                                <h3 className="text-2xl font-bold mb-1">
                                                    {selectedProduct.itemName}
                                                </h3>
                                                <p className={clsx(
                                                    "text-sm font-mono",
                                                    isDark ? "text-gray-400" : "text-gray-500"
                                                )}>
                                                    {isArabic ? "الكود: " : "Code: "}
                                                    <span className="font-semibold">{selectedProduct.itemCode}</span>
                                                </p>
                                            </div>

                                            {/* السعر الأساسي */}
                                            <div className="text-right">

                                                {/* ابحث عن السعر في الـ Header واستبدله بهذا */}
                                                <div className="text-right">
                                                    <div className="text-xs text-gray-500 mb-1">{isArabic ? "السعر" : "Price"}</div>
                                                    <div className="text-2xl font-bold text-[#5B2C6F]">
                                                        {(() => {
                                                            const p = selectedProduct.variants?.[0]?.price || 0;
                                                            const d = selectedProduct.discountValue || 0;
                                                            const dt = selectedProduct.discountType || 0;
                                                            const final = getDiscountedPrice(p, d, dt);
                                                            return (
                                                                <div className="flex flex-col items-end">
                                                                    {d > 0 && <span className="text-sm line-through text-gray-400 font-normal">{p}</span>}
                                                                    <span>{parseFloat(final.toFixed(2))}</span>
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* الوصف */}
                                        {selectedProduct.description && (
                                            <div className={clsx(
                                                "mt-3 p-3 rounded-lg text-sm",
                                                isDark ? "bg-gray-800/50" : "bg-gray-50"
                                            )}>
                                                <div className="flex items-start gap-2">
                                                    <span className="text-base"> {isArabic ? "الوصف" : "Description"}📝</span>
                                                    <p className="text-sm leading-relaxed flex-1">
                                                        {selectedProduct.description}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* الخطوة 1: اختيار اللون */}
                                <div>
                                    <label className="block text-sm font-bold mb-3 text-lg">
                                        {isArabic ? "1️⃣ اختر اللون" : "1️⃣ Select Color"}
                                    </label>
                                    <div className="grid gap-4">
                                        {(() => {
                                            // تجميع المتغيرات حسب اللون
                                            const colorGroups = {};
                                            selectedProduct.variants?.forEach(v => {
                                                const colorKey = v.color?.hexCode || v.color?.name || 'no-color';
                                                const colorName = v.color?.name || "";

                                                if (!colorGroups[colorKey]) {
                                                    colorGroups[colorKey] = {
                                                        colorHex: colorKey,
                                                        colorName: colorName,
                                                        variants: [],
                                                        price: v.price,
                                                        costPrice: v.costPrice,
                                                        imageUrl: v.imageUrl // صورة المتغير
                                                    };
                                                }
                                                colorGroups[colorKey].variants.push(v);
                                            });

                                            return Object.values(colorGroups).map((group, idx) => {
                                                const isSelected = selectedVariant &&
                                                    (selectedVariant.color?.hexCode === group.colorHex ||
                                                        selectedVariant.color?.name === group.colorHex);
                                                const groupOriginalPrice = group.price;
                                                // الخصم دائماً من selectedProduct
                                                const groupDiscount = selectedProduct.discountValue || 0;
                                                const groupDiscountType = selectedProduct.discountType || 0;
                                                const groupFinalPrice = getDiscountedPrice(groupOriginalPrice, groupDiscount, groupDiscountType);
                                                // حساب إجمالي المخزون لهذا اللون
                                                const totalStock = group.variants.reduce((sum, v) => {
                                                    return sum + (v.sizes?.reduce((s, sz) => s + (sz.stockQty || 0), 0) || 0);
                                                }, 0);
                                                const isOutOfStock = totalStock <= 0;

                                                // جمع كل الأحجام المتاحة
                                                const allSizes = [];
                                                group.variants.forEach(v => {
                                                    if (v.sizes) {
                                                        v.sizes.forEach(sz => {
                                                            allSizes.push({
                                                                ...sz,
                                                                variantId: v.variantId,
                                                                variant: v
                                                            });
                                                        });
                                                    }
                                                });

                                                // بناء رابط الصورة
                                                const activeImageUrl = group.imageUrl || selectedProduct?.mainImageUrl;
                                                const variantImageUrl = activeImageUrl
                                                    ? (activeImageUrl.startsWith('http')
                                                        ? activeImageUrl
                                                        : `${baseImageUrl}${activeImageUrl}`)
                                                    : "https://placehold.co/150x150/e0e0e0/666?text=No+Image";

                                                return (
                                                    <div
                                                        key={idx}
                                                        onClick={() => {
                                                            if (!isOutOfStock) {
                                                                setSelectedVariant(group.variants[0]);
                                                                setSelectedSize(null); // إعادة تعيين الحجم
                                                            }
                                                        }}
                                                        className={clsx(
                                                            "rounded-xl border-2 transition-all overflow-hidden",
                                                            isOutOfStock ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:shadow-xl",
                                                            isSelected
                                                                ? "border-[#5B2C6F] ring-2 ring-[#5B2C6F]"
                                                                : (!isOutOfStock && (isDark ? "border-gray-700 hover:border-[#5B2C6F]/50" : "border-gray-300 hover:border-[#5B2C6F]/50"))
                                                        )}
                                                    >
                                                        <div className="flex">
                                                            {/* الصورة */}
                                                            <div className="w-32 h-32 flex-shrink-0 bg-gray-100 dark:bg-gray-800 relative">
                                                                <img
                                                                    src={variantImageUrl}
                                                                    alt={group.colorName || selectedProduct.itemName}
                                                                    className="w-full h-full object-cover"
                                                                />
                                                                {isSelected && (
                                                                    <div className="absolute top-2 right-2 bg-[#5B2C6F] text-white rounded-full p-1.5">
                                                                        <FiCheck size={16} />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* المعلومات */}
                                                            <div className="flex-1 p-4 flex flex-col justify-between">
                                                                <div>
                                                                    {/* اسم المنتج واللون */}
                                                                    <div className="flex items-start justify-between gap-3 mb-2">
                                                                        <div className="flex items-center gap-2">
                                                                            <div
                                                                                className="w-6 h-6 rounded-full border-2 border-gray-300 shadow-sm flex-shrink-0"
                                                                                style={{ backgroundColor: group.colorHex }}
                                                                            />
                                                                            <div>
                                                                                <h4 className="font-bold text-base leading-tight">
                                                                                    {selectedProduct.itemName}
                                                                                </h4>
                                                                                {group.colorName && (
                                                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                                                        {isArabic ? "اللون: " : "Color: "}{group.colorName}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </div>

                                                                        {/* السعر */}
                                                                        <div className="text-right flex-shrink-0">
                                                                            {groupDiscount > 0 && (
                                                                                <div className="text-xs line-through text-gray-400">
                                                                                    {groupOriginalPrice}
                                                                                </div>
                                                                            )}
                                                                            <div className="font-bold text-lg text-[#5B2C6F]">
                                                                                {parseFloat(groupFinalPrice.toFixed(2))}
                                                                            </div>
                                                                            <div className="text-[9px] text-gray-400">
                                                                                {isArabic ? "د.ع" : "IQD"}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    {/* الوصف */}
                                                                    {selectedProduct.description && (
                                                                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                                                                            {selectedProduct.description}
                                                                        </p>
                                                                    )}

                                                                    {/* معلومات إضافية */}
                                                                    <div className="flex items-center gap-3 flex-wrap text-xs">
                                                                        {group.variants[0].memory?.name && (
                                                                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded-md">
                                                                                📱 {group.variants[0].memory.name}
                                                                            </span>
                                                                        )}
                                                                        <span className="px-2 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md">
                                                                            📏 {allSizes.length} {isArabic ? "حجم" : "sizes"}
                                                                        </span>
                                                                        <span className={clsx(
                                                                            "px-2 py-1 rounded-md font-medium",
                                                                            !isOutOfStock
                                                                                ? "bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                                                                                : "bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300"
                                                                        )}>
                                                                            📦 {!isOutOfStock
                                                                                ? `${totalStock} ${isArabic ? "قطعة" : "pcs"}`
                                                                                : (isArabic ? "نفذ من المخزون" : "Out of stock")
                                                                            }
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            });
                                        })()}
                                    </div>
                                </div>

                                {/* الخطوة 2: اختيار الحجم */}
                                {selectedVariant && (
                                    <div className={clsx(
                                        "p-4 rounded-xl border-2 border-[#5B2C6F] bg-[#5B2C6F]/5",
                                        "animate-in fade-in duration-200"
                                    )}>
                                        <label className="block text-sm font-bold mb-3 text-lg">
                                            {isArabic ? "2️⃣ اختر الحجم" : "2️⃣ Select Size"}
                                        </label>
                                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                            {(() => {
                                                // جمع كل الأحجام من المتغيرات التي لها نفس اللون
                                                const selectedColorHex = selectedVariant.color?.hexCode || selectedVariant.color?.name;
                                                const relatedVariants = selectedProduct.variants.filter(v =>
                                                    (v.color?.hexCode === selectedColorHex || v.color?.name === selectedColorHex)
                                                );

                                                const allSizes = [];
                                                relatedVariants.forEach(v => {
                                                    if (v.sizes) {
                                                        v.sizes.forEach(sz => {
                                                            allSizes.push({
                                                                ...sz,
                                                                variantId: v.variantId,
                                                                variant: v
                                                            });
                                                        });
                                                    }
                                                });

                                                return allSizes.map((sz, idx) => {
                                                    const isSelected = selectedSize?.size === sz.size && selectedSize?.variantId === sz.variantId;
                                                    const isAvailable = (sz.stockQty || 0) > 0;

                                                    return (
                                                        <button
                                                            key={idx}
                                                            type="button"
                                                            disabled={!isAvailable}
                                                            onClick={() => {
                                                                setSelectedSize(sz);
                                                                setSelectedVariant(sz.variant);
                                                            }}
                                                            className={clsx(
                                                                "px-4 py-3 rounded-lg border-2 text-sm font-bold transition-all",
                                                                isAvailable ? "cursor-pointer" : "opacity-40 cursor-not-allowed",
                                                                isSelected
                                                                    ? "border-[#5B2C6F] bg-[#5B2C6F] text-white shadow-lg scale-105"
                                                                    : isAvailable
                                                                        ? (isDark
                                                                            ? "border-gray-600 bg-gray-700 text-white hover:border-[#5B2C6F]"
                                                                            : "border-gray-300 bg-white text-gray-900 hover:border-[#5B2C6F]")
                                                                        : "border-gray-300 bg-gray-100 text-gray-400"
                                                            )}
                                                        >
                                                            <div className="flex flex-col items-center gap-1">
                                                                <span className="text-base">{sz.size} {isArabic ? "الحجم " : "Size "}</span>
                                                                <span className={clsx(
                                                                    "text-[10px]",
                                                                    isSelected ? "text-white" : "text-gray-500"
                                                                )}>
                                                                    {sz.stockQty || 0} {isArabic ? "قطعة" : "pcs"}
                                                                </span>
                                                            </div>
                                                        </button>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                )}

                                {/* الخطوة 3: الكمية */}
                                {selectedSize && (
                                    <div className="animate-in fade-in duration-200">
                                        <label className="block text-sm font-bold mb-3 text-lg">
                                            {isArabic ? "3️⃣ أدخل الكمية" : "3️⃣ Enter Quantity"}
                                        </label>
                                        <input
                                            type="number"
                                            min="1"
                                            max={selectedSize.stockQty || 1}
                                            value={qty}
                                            onChange={e => setQty(e.target.value)}
                                            className={clsx(
                                                "w-full px-4 py-3 rounded-xl border-2 text-center text-lg font-bold",
                                                isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
                                            )}
                                        />
                                        <p className="text-xs text-gray-500 mt-2 text-center">
                                            {isArabic
                                                ? `الحد الأقصى: ${selectedSize.stockQty || 0}`
                                                : `Max: ${selectedSize.stockQty || 0}`}
                                        </p>
                                    </div>
                                )}

                                {/* زر الإضافة */}
                                <button
                                    disabled={!selectedVariant || !selectedSize || Number(selectedSize?.stockQty || 0) <= 0}
                                    onClick={handleAddToLocalCart}
                                    className="w-full py-4 mt-4 rounded-xl text-white font-bold text-lg transition-all shadow-lg disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                                    style={{ backgroundColor: "#5B2C6F" }}
                                >
                                    <FiShoppingCart size={24} />
                                    <span>
                                        {Number(selectedSize?.stockQty || 0) <= 0
                                            ? (isArabic ? "انتهى من المخزون" : "Out of stock")
                                            : (isArabic ? "إضافة للقائمة" : "Add to List")}
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Toolbar */}
                <div className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6">
                    <div className="relative w-full sm:w-96">
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-400">
                            <FiSearch />
                        </div>
                        <input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className={clsx(
                                "w-full pl-4 pr-10 py-2.5 rounded-xl border outline-none transition-all",
                                isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-200"
                            )}
                            placeholder={isArabic ? "بحث عن منتج..." : "Search products..."}
                        />
                    </div>
                </div>

                {/* Product Grid */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {filteredProducts.map(product => {
                        const image = getProductImage(product);
                        const minPrice = Math.min(...(product.variants?.map(v => v.price) || [0]));
                        const totalProductStock = getProductTotalStock(product);
                        const isProductOutOfStock = totalProductStock <= 0;
                        {/* داخل filteredProducts.map */ }
                        const firstVar = product.variants?.[0];
                        const originalPrice = firstVar?.price || 0;
                        const dValue = product.discountValue || 0;
                        const dType = product.discountType || 0;
                        const finalPrice = getDiscountedPrice(originalPrice, dValue, dType);

                        // التعديل هنا (نأخذ الخصم من الـ product مباشرة)


                        return (
                            <div
                                key={product.itemId}
                                onClick={() => {
                                    if (!isProductOutOfStock) {
                                        setSelectedProduct(product);
                                    }
                                }}
                                className={clsx(
                                    "group relative rounded-2xl border overflow-hidden cursor-pointer transition-all ",
                                    isProductOutOfStock ? "opacity-60 cursor-not-allowed" : "",
                                    isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
                                )}
                            >
                                {/* Image */}
                                <div className="aspect-square bg-gray-100 dark:bg-gray-900 relative">
                                    <img
                                        src={image}
                                        alt={product.itemName}
                                        className={clsx(
                                            "w-full h-full object-cover transition-transform ",
                                            isProductOutOfStock ? "grayscale" : ""
                                        )}
                                    />
                                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                                        {isArabic ? "الخيارات" : "Options"}{product.variants?.length || 0}
                                    </div>
                                    {isProductOutOfStock && (
                                        <div className="absolute bottom-2 right-2 bg-red-600/90 text-white text-xs px-2 py-1 rounded-md">
                                            {isArabic ? "نفذ من المخزون" : "Out of stock"}
                                        </div>
                                    )}
                                </div>

                                {/* Details */}
                                <div className="p-4">
                                    <h3 className={clsx("font-bold text-sm mb-1 truncate", isDark ? "text-gray-100" : "text-gray-900")}>
                                        {isArabic ? "الاسم" : "Name"}:
                                        {product.itemName}
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-2 truncate">{isArabic ? "القسم" : "Category"} :{product.category?.name || "-"}</p>
                                    <div className="font-bold text-[#5B2C6F]">
                                        {isArabic ? "السعر: " : "Price: "}
                                        {dValue > 0 && (
                                            <span className="text-xs line-through text-gray-400 mr-1 ml-1 font-normal">
                                                {originalPrice}
                                            </span>
                                        )}
                                        <span>{parseFloat(finalPrice.toFixed(2))}</span>
                                        <span className="text-xs"> د.ع</span>
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {filteredProducts.length === 0 && (
                    <div className="py-20 text-center text-gray-400">
                        <p>{isArabic ? "لا توجد منتجات" : "No products found"}</p>
                    </div>
                )}

            </PageContainer>
        );
    }

    // PHASE 2: DETAILS FORM
    return (
        <PageContainer title={isArabic ? "إكمال تفاصيل الطلب" : "Complete Order Details"}>
            <div className={clsx("max-w-4xl mx-auto rounded-2xl p-6 border mb-10", isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-200")}>

                <button
                    onClick={() => setPhase(1)}
                    className="mb-6 text-sm text-gray-400 flex items-center gap-1"
                >
                    ← {isArabic ? "العودة للمنتجات" : "Back to Products"}
                </button>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* Form */}
                    <form onSubmit={handleFinalizeOrder} className="space-y-4">
                        <h3 className="font-bold text-lg mb-4">{isArabic ? "بيانات العميل" : "Customer Data"}</h3>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">{isArabic ? "اسم العميل" : "Customer Name"}</label>
                                <input
                                    name="customerName"
                                    required
                                    value={formData.customerName}
                                    onChange={e => setFormData({ ...formData, customerName: e.target.value })}
                                    className={clsx("w-full px-4 py-2 rounded-xl border", isDark ? "bg-gray-800 border-gray-700" : "bg-white")}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{isArabic ? "رقم الهاتف" : "Phone"}</label>
                                <input
                                    name="customerPhone1"
                                    required
                                    value={formData.customerPhone1}
                                    onChange={e => setFormData({ ...formData, customerPhone1: e.target.value })}
                                    className={clsx("w-full px-4 py-2 rounded-xl border", isDark ? "bg-gray-800 border-gray-700" : "bg-white")}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{isArabic ? "المحافظة/المدينة" : "City"}</label>
                                <select
                                    name="city"
                                    required
                                    value={formData.city}
                                    onChange={e => setFormData({ ...formData, city: e.target.value })}
                                    className={clsx("w-full px-4 py-2 rounded-xl border outline-none", isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300")}
                                >
                                    <option value="">{isArabic ? "اختر المحافظة" : "Select City"}</option>
                                    {cities.map(c => (
                                        <option key={c.cityId} value={c.cityName}>{c.cityName}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">{isArabic ? "العنوان" : "Address"}</label>
                                <input
                                    name="address"
                                    value={formData.address}
                                    onChange={e => setFormData({ ...formData, address: e.target.value })}
                                    className={clsx("w-full px-4 py-2 rounded-xl border", isDark ? "bg-gray-800 border-gray-700" : "bg-white")}
                                />
                            </div>
                        </div>

                        <div className="pt-6">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex items-center justify-center gap-2 px-8 py-3 rounded-xl text-white font-bold transition disabled:opacity-50 disabled:cursor-not-allowed text-lg"
                                style={{ backgroundColor: "#5B2C6F" }}
                            >
                                {loading ? "..." : (isArabic ? "حفظ الطلب" : "Save Order")}
                            </button>
                        </div>
                    </form>

                    {/* Cart Review */}
                    <div className={clsx("rounded-2xl p-6 border h-fit", isDark ? "bg-[#0f172a] border-gray-700" : "bg-gray-50 border-gray-200")}>
                        <h3 className="font-bold text-lg mb-4">{isArabic ? "ملخص الطلب" : "Order Summary"}</h3>
                        <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                            {cartItems.map((item, idx) => (
                                <div key={idx} className="flex justify-between items-start pb-3 border-b border-gray-200 dark:border-gray-700 last:border-0 relative group">
                                    <div className="flex gap-3">
                                        <div className="w-12 h-12 bg-white rounded-lg border overflow-hidden flex items-center justify-center">
                                            {item.imageUrl ? (
                                                <img
                                                    src={item.imageUrl.startsWith('http') ? item.imageUrl : `${baseImageUrl}${item.imageUrl}`}
                                                    className="w-full h-full object-cover"
                                                    alt={item.itemName}
                                                />
                                            ) : (
                                                <span className="text-[10px] text-gray-400">No Image</span>
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-bold text-sm line-clamp-1">{item.itemName}</p>
                                            <p className="text-xs text-gray-500">
                                                {item.color} {item.size ? ` / ${item.size}` : ""} x {item.quantity}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-bold text-sm text-[#5B2C6F]">{item.total} د.ع</p>
                                        <div className="flex items-center justify-end gap-2 mt-1">
                                            <button
                                                type="button"
                                                onClick={() => handleUpdateLocalQty(item.tempid, item.quantity - 1)}
                                                className="w-6 h-6 rounded-md border text-xs flex items-center justify-center text-gray-500"
                                            >
                                                <FiMinus size={12} />
                                            </button>
                                            <span className="text-xs font-bold min-w-[20px] text-center">{item.quantity}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleUpdateLocalQty(item.tempid, item.quantity + 1)}
                                                className="w-6 h-6 rounded-md border text-xs flex items-center justify-center text-gray-500"
                                            >
                                                <FiPlus size={12} />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveItem(item.tempid)}
                                                className="text-red-500 text-xs"
                                            >
                                                {isArabic ? "حذف" : "Remove"}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="pt-4 mt-4 border-t border-gray-300 dark:border-gray-600 space-y-2">
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">{isArabic ? "المجموع الفرعي" : "Subtotal"}</span>
                                <span>{cartItems.reduce((acc, i) => acc + i.total, 0).toLocaleString()} د.ع</span>
                            </div>
                            <div className="flex justify-between items-center text-sm">
                                <span className="text-gray-500">{isArabic ? "أجور التوصيل" : "Delivery Fees"}</span>
                                <span className={formData.deliveryFee === 0 ? "text-green-500 font-bold" : ""}>
                                    {formData.deliveryFee === 0 ? (isArabic ? "مجاني" : "Free") : `${formData.deliveryFee.toLocaleString()} د.ع`}
                                </span>
                            </div>
                            <div className="flex justify-between items-center text-xl font-bold pt-2 border-t border-gray-100 dark:border-gray-800">
                                <span>{isArabic ? "المجموع الكلي" : "Total"}</span>
                                <span>{(cartItems.reduce((acc, i) => acc + i.total, 0) + (Number(formData.deliveryFee) || 0)).toLocaleString()} د.ع</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </PageContainer>
    );
}
